import React, { useEffect, useMemo } from 'react';
import { CEBProductFlags } from 'components/asset-access/useProductFlags';

import { useAssetAccess } from 'components/asset-access';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { matchPath, useLocation } from 'react-router';

export interface NavItemData {
  title: string;
  to: string;
  pathPattern?: string;
  matchPath?: string | string[];
  end?: boolean;
  logo?: React.ReactNode;
  headerSection?: React.ReactNode;
  searchPath?: string;
  searchLabel?: React.ReactNode | string;
  type?: string;
  items?: Omit<NavItemData, 'title'>[];
  hideSearchFilters?: boolean;
  hidden?: boolean;
}

export type ChildNavItem = NonNullable<NavItemData['items']>[number];

type NavGroupProps = NavItemData & {
  children?: React.ReactNode;
};

type NavItemProps = Omit<NavGroupProps, 'title'> & {
  children?: React.ReactNode | string;
};

const NavGroupContext = React.createContext<{
  addItem: (item: Omit<NavItemData, 'title'>) => void;
  removeItem: (item: Omit<NavItemData, 'title'>) => void;
} | null>(null);

const definedNavItemsAtom = atom<NavItemData[]>([]);
const activeNav = atom<
  { group?: NavItemData; item?: ChildNavItem; search?: string } | undefined
>(undefined);

export function matchPaths(
  matchPattern: string | string[] | undefined,
  pathname: string,
  end?: boolean,
): boolean {
  if (!matchPattern) return false;

  if (Array.isArray(matchPattern)) {
    return matchPattern.some(path => matchPath({ path, end }, pathname));
  }

  return matchPath({ path: matchPattern, end }, pathname) !== null;
}

function NavRouteMatch() {
  const { pathname } = useLocation();
  const groups = useAtomValue(definedNavItemsAtom);
  const setActiveNav = useSetAtom(activeNav);

  const group = useMemo(
    () =>
      groups.filter(g => matchPaths(g.matchPath, pathname, g.end)).slice(-1)[0],
    [groups, pathname],
  );

  const item = useMemo(
    () =>
      group?.items?.find(
        item =>
          matchPaths(item.matchPath, pathname, item.end) ||
          pathname === item.searchPath,
      ),
    [group, pathname],
  );

  const search = useMemo(
    () =>
      item?.searchPath || group?.items.filter(i => i.searchPath)[0]?.searchPath,
    [group, item],
  );

  React.useEffect(() => {
    setActiveNav({ group, item, search });
  }, [group, item, search, setActiveNav]);

  return null;
}

export function NavItem(props: NavItemProps) {
  const context = React.useContext(NavGroupContext);

  const item: Omit<NavItemData, 'title'> = useMemo(() => {
    const { children, ...rest } = props;
    return { searchLabel: children, ...rest };
  }, [props]);

  useEffect(() => {
    context?.addItem(item);
    return () => {
      context?.removeItem(item);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return null;
}

export function NavGroup({ children, ...props }: NavGroupProps) {
  const setNavGroups = useSetAtom(definedNavItemsAtom);

  const [navItems, setNavItems] = React.useState<Omit<NavItemData, 'title'>[]>(
    [],
  );

  const addItem = React.useCallback(
    (item: Omit<NavItemData, 'title'>) => {
      setNavItems(prev => [...prev, item]);
    },
    [setNavItems],
  );

  const removeItem = React.useCallback(
    (item: Omit<NavItemData, 'title'>) => {
      setNavItems(prev => prev.filter(i => i !== item));
    },
    [setNavItems],
  );

  React.useLayoutEffect(() => {
    const group = { ...props, items: navItems };
    setNavGroups(prev => [...prev, group]);
    return () => {
      setNavGroups(prev => prev.filter(g => g !== group));
    };
  }, [props, navItems, setNavGroups]);

  return (
    <NavGroupContext.Provider value={{ addItem, removeItem }}>
      {children}
      <NavRouteMatch />
    </NavGroupContext.Provider>
  );
}

export function AccessCheck({
  flag,
  productCode,
  children,
}: {
  flag?: keyof CEBProductFlags;
  productCode?: string | null;
  children: React.ReactNode;
}) {
  const assetAccess = useAssetAccess();

  if (flag) {
    return assetAccess[flag] ? children : null;
  }

  if (productCode) {
    return assetAccess.hasProductCode && assetAccess.hasProductCode(productCode)
      ? children
      : null;
  }

  return children;
}

export function useActiveNav() {
  return useAtomValue(activeNav);
}

export function useAccessibleNavItems() {
  const groups = useAtomValue(definedNavItemsAtom);
  const { group } = useActiveNav() || {};
  const searchableNavItems = React.useMemo(() => {
    return group
      ? group.items
          .flatMap(g => [g, ...(g.items || [])])
          .filter(item => item.searchPath)
      : [];
  }, [group]);

  return {
    searchable: searchableNavItems,
    all: groups,
  };
}
